import { FC, useEffect, useState } from 'react';
import { Box, Grid, Link, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import {
  chinaFooterNoticeContainer,
  chinaFooterNoticeLink,
  chinaFooterNoticeText,
  FooterContainer,
  footerCopyright,
  FooterLink,
  FooterLinksContainer,
  FooterSectionContainer,
  MenuContainer,
  MenuLink,
  MenuSectionContainer,
  MenuTitle
} from './Footer.styles';
import { useFooterLegalLinks, useFooterMenuLinks } from '@/hooks/footerLinks';
import { sopClient } from '@barracuda/shared';
import { isChinaRegion } from '@barracuda/shared/utils/feature-flags';
import { FooterLinkItem } from './FooterLinkItem';

export const Footer: FC = () => {
  const { t } = useTranslation();
  const menuLinks = useFooterMenuLinks();
  const legalLinks = useFooterLegalLinks();
  const [icpLicenseNumber, setICPLicenseNumber] = useState<string | null>(null);
  const helpCenterLink = process.env.REACT_APP_HELP_CENTER_LINK;

  useEffect(() => {
    (async () => {
      setICPLicenseNumber(await sopClient.fetchICPLicenseNumber());
    })();
  }, []);

  return (
    <Box component='footer'>
      <Box sx={MenuContainer}>
        <Grid sx={MenuSectionContainer} container direction='column'>
          <Grid item xs>
            <Typography sx={MenuTitle}>{t('menuLabels.menuTitle')}</Typography>
          </Grid>
          {menuLinks.map((link, index) => (
            <Grid item xs key={`menu-link-${index}`}>
              <FooterLinkItem link={link} sx={MenuLink} />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box sx={FooterContainer}>
        <Grid sx={FooterSectionContainer} container>
          <Grid item xs={12} md={6}>
            <Box sx={FooterLinksContainer}>
              {legalLinks.map((link, index) => (
                <FooterLinkItem key={`footer-link-${index}`} link={link} sx={FooterLink} />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography sx={footerCopyright}>
              {t('allRightsReserved', { year: new Date().getFullYear() })}
              &nbsp;
              {icpLicenseNumber && t('icpRegistration', { ICPLicenseNumber: icpLicenseNumber })}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {isChinaRegion() && helpCenterLink && (
        <Box sx={chinaFooterNoticeContainer}>
          <Box sx={{ maxWidth: '1320px', width: '100%' }}>
            <Typography sx={chinaFooterNoticeText}>
              <Trans
                i18nKey='footerChinaNotice.message'
                components={{
                  bold: <Typography component='span' sx={{ fontWeight: 700 }} />,
                  helpCenter: (
                    <Link
                      sx={chinaFooterNoticeLink}
                      href={helpCenterLink}
                      target='_blank'
                      rel='noopener noreferrer'
                    />
                  )
                }}
              />
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
